"use client";

import { useState, useCallback } from "react";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

type Platform = "x" | "linkedin" | "facebook" | "whatsapp" | "telegram" | "email" | "copy";

interface ShareBarProps {
  /** URL to share (defaults to current page) */
  url?: string;
  /** Title/text for share dialogs */
  title?: string;
  /** Short description (used by email & native share) */
  description?: string;
  /** Which buttons to show and in what order */
  platforms?: Platform[];
  /** Show the native Web Share API button on supported devices */
  showNativeShare?: boolean;
  /** Visual variant */
  variant?: "default" | "compact" | "minimal";
  /** Icon size in pixels */
  iconSize?: number;
  /** Additional CSS class for the wrapper */
  className?: string;
  /** Show labels next to icons (only for default variant) */
  showLabels?: boolean;
}

// ─────────────────────────────────────────────
// Platform configs
// ─────────────────────────────────────────────

const PLATFORM_CONFIG: Record<
  Platform,
  {
    label: string;
    labelPl: string;
    color: string;
    hoverColor: string;
    buildUrl?: (url: string, title: string, description: string) => string;
  }
> = {
  x: {
    label: "X / Twitter",
    labelPl: "X / Twitter",
    color: "#0C1F3F",
    hoverColor: "#00A87A",
    buildUrl: (url, title) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  linkedin: {
    label: "LinkedIn",
    labelPl: "LinkedIn",
    color: "#0C1F3F",
    hoverColor: "#00A87A",
    buildUrl: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  facebook: {
    label: "Facebook",
    labelPl: "Facebook",
    color: "#0C1F3F",
    hoverColor: "#00A87A",
    buildUrl: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  whatsapp: {
    label: "WhatsApp",
    labelPl: "WhatsApp",
    color: "#0C1F3F",
    hoverColor: "#00A87A",
    buildUrl: (url, title) =>
      `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
  },
  telegram: {
    label: "Telegram",
    labelPl: "Telegram",
    color: "#0C1F3F",
    hoverColor: "#00A87A",
    buildUrl: (url, title) =>
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  email: {
    label: "Email",
    labelPl: "E-mail",
    color: "#0C1F3F",
    hoverColor: "#00A87A",
    buildUrl: (url, title, description) =>
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description}\n\n${url}`)}`,
  },
  copy: {
    label: "Copy link",
    labelPl: "Kopiuj link",
    color: "#0C1F3F",
    hoverColor: "#00A87A",
  },
};

// ─────────────────────────────────────────────
// SVG Icons (inline, no dependency needed)
// ─────────────────────────────────────────────

function IconX({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function IconLinkedIn({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function IconFacebook({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function IconWhatsApp({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function IconTelegram({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

function IconEmail({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function IconCopy({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function IconCheck({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconShare({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

const ICON_MAP: Record<Platform, React.FC<{ size?: number }>> = {
  x: IconX,
  linkedin: IconLinkedIn,
  facebook: IconFacebook,
  whatsapp: IconWhatsApp,
  telegram: IconTelegram,
  email: IconEmail,
  copy: IconCopy,
};

// ─────────────────────────────────────────────
// ShareBar component
// ─────────────────────────────────────────────

export default function ShareBar({
  url,
  title = "",
  description = "",
  platforms = ["x", "linkedin", "facebook", "copy"],
  showNativeShare = true,
  variant = "default",
  iconSize,
  className = "",
  showLabels = false,
}: ShareBarProps) {
  const [copied, setCopied] = useState(false);
  const [canNativeShare] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!navigator.share;
  });

  const resolvedUrl = url ?? (typeof window !== "undefined" ? window.location.href : "");

  const resolvedIconSize = iconSize ?? (variant === "compact" ? 16 : variant === "minimal" ? 14 : 18);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(resolvedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = resolvedUrl;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [resolvedUrl]);

  const handleNativeShare = useCallback(async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title,
        text: description || title,
        url: resolvedUrl,
      });
    } catch (err) {
      // User cancelled or error — fail silently
      if (err instanceof Error && err.name !== "AbortError") {
        console.error("Share failed:", err);
      }
    }
  }, [title, description, resolvedUrl]);

  // ── Styles ──

  const wrapperStyles: Record<string, string> = {
    default: "sb-wrapper sb-default",
    compact: "sb-wrapper sb-compact",
    minimal: "sb-wrapper sb-minimal",
  };

  return (
    <>
      <style>{`
        .sb-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .sb-wrapper.sb-compact { gap: 6px; }
        .sb-wrapper.sb-minimal { gap: 4px; }

        .sb-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border: none;
          cursor: pointer;
          font-family: inherit;
          text-decoration: none;
          transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          position: relative;
          white-space: nowrap;
          -webkit-tap-highlight-color: transparent;
        }

        /* ── Default variant ── */
        .sb-default .sb-btn {
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          background: #fff;
          color: var(--sb-fg, #0C1F3F);
          border: 1.5px solid var(--sb-border, #E5E7EB);
          line-height: 1;
        }
        .sb-default .sb-btn:hover {
          background: var(--sb-bg-hover, #E8FAF4);
          color: var(--sb-fg-hover, #0C1F3F);
          border-color: var(--sb-border-hover, #00A87A);
          box-shadow: 0 10px 24px -18px rgba(12, 31, 63, 0.32);
        }
        .sb-default .sb-btn:active {
          box-shadow: none;
        }

        /* ── Compact variant ── */
        .sb-compact .sb-btn {
          padding: 6px;
          border-radius: 6px;
          background: #fff;
          color: var(--sb-fg, #0C1F3F);
          border: 1.5px solid var(--sb-border, #E5E7EB);
        }
        .sb-compact .sb-btn:hover {
          background: var(--sb-bg-hover, #E8FAF4);
          color: var(--sb-fg-hover, #0C1F3F);
          border-color: var(--sb-border-hover, #00A87A);
          box-shadow: 0 10px 24px -18px rgba(12, 31, 63, 0.32);
        }

        /* ── Minimal variant ── */
        .sb-minimal .sb-btn {
          padding: 4px;
          border-radius: 4px;
          background: transparent !important;
          color: var(--sb-fg, #5C6370);
          opacity: 0.8;
        }
        .sb-minimal .sb-btn:hover {
          background: var(--sb-bg-hover, transparent) !important;
          color: var(--sb-fg-hover, #00A87A);
          opacity: 1;
        }

        /* ── Copied state ── */
        .sb-btn--copied {
          pointer-events: none;
        }

        /* ── Separator ── */
        .sb-separator {
          width: 1px;
          height: 20px;
          background: currentColor;
          opacity: 0.15;
          flex-shrink: 0;
        }
        .sb-compact .sb-separator { height: 16px; }
        .sb-minimal .sb-separator { height: 14px; }

        /* ── Focus visible ── */
        .sb-btn:focus-visible {
          outline: 2px solid #00A87A;
          outline-offset: 2px;
        }
      `}</style>

      <div
        className={`${wrapperStyles[variant]} ${className}`}
        role="group"
        aria-label="Udostępnij"
      >
        {/* Native share button (mobile-first) */}
        {showNativeShare && canNativeShare && (
          <>
            <button
              className="sb-btn sb-btn--native"
              onClick={handleNativeShare}
              aria-label="Udostępnij"
              style={
                variant === "minimal"
                  ? ({
                      "--sb-fg": "#0C1F3F",
                      "--sb-fg-hover": "#00A87A",
                    } as React.CSSProperties)
                  : ({
                      "--sb-fg": "#0C1F3F",
                      "--sb-fg-hover": "#0C1F3F",
                      "--sb-border": "#E5E7EB",
                      "--sb-border-hover": "#00A87A",
                      "--sb-bg-hover": "#E8FAF4",
                    } as React.CSSProperties)
              }
            >
              <IconShare size={resolvedIconSize} />
              {variant === "default" && showLabels && <span>Udostępnij</span>}
            </button>
            <div className="sb-separator" />
          </>
        )}

        {/* Per-platform buttons */}
        {platforms.map((platform) => {
          const config = PLATFORM_CONFIG[platform];
          const Icon = ICON_MAP[platform];

          if (platform === "copy") {
            return (
              <button
                key="copy"
                className={`sb-btn ${copied ? "sb-btn--copied" : ""}`}
                onClick={handleCopy}
                aria-label={copied ? "Skopiowano!" : config.labelPl}
                title={copied ? "Skopiowano!" : config.labelPl}
                style={
                  variant === "minimal"
                    ? ({
                        "--sb-fg": copied ? "#00A87A" : "#5C6370",
                        "--sb-fg-hover": "#00A87A",
                      } as React.CSSProperties)
                    : ({
                        "--sb-fg": copied ? "#00A87A" : config.color,
                        "--sb-fg-hover": "#0C1F3F",
                        "--sb-border": copied ? "#00A87A" : "#E5E7EB",
                        "--sb-border-hover": "#00A87A",
                        "--sb-bg-hover": "#E8FAF4",
                      } as React.CSSProperties)
                }
              >
                {copied ? <IconCheck size={resolvedIconSize} /> : <Icon size={resolvedIconSize} />}
                {variant === "default" && showLabels && (
                  <span>{copied ? "Skopiowano!" : config.labelPl}</span>
                )}
              </button>
            );
          }

          const href = config.buildUrl?.(resolvedUrl, title, description);
          if (!href) return null;

          const isMailto = platform === "email";

          return (
            <a
              key={platform}
              href={href}
              className="sb-btn"
              target={isMailto ? undefined : "_blank"}
              rel={isMailto ? undefined : "noopener noreferrer"}
              aria-label={`Udostępnij na ${config.label}`}
              title={config.label}
              style={
                variant === "minimal"
                  ? ({
                      "--sb-fg": "#5C6370",
                      "--sb-fg-hover": config.hoverColor,
                    } as React.CSSProperties)
                  : ({
                      "--sb-fg": config.color,
                      "--sb-fg-hover": "#0C1F3F",
                      "--sb-border": "#E5E7EB",
                      "--sb-border-hover": config.hoverColor,
                      "--sb-bg-hover": "#E8FAF4",
                    } as React.CSSProperties)
              }
            >
              <Icon size={resolvedIconSize} />
              {variant === "default" && showLabels && <span>{config.labelPl}</span>}
            </a>
          );
        })}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
// Named exports for granular use
// ─────────────────────────────────────────────

export { ShareBar };
export type { ShareBarProps, Platform };
